/**
 * Benchmark-based scoring system for model recommendations
 */

import type { WizardPersona, WizardEnvironment } from '@/stores/wizardStore';

export interface ModelScore {
  modelId: string;
  score: number;
  reasoning: string[];
  strengths: string[];
  considerations: string[];
  benchmarkBreakdown?: {
    mmlu?: number;
    humaneval?: number;
    gpqa?: number;
    arena_elo?: number;
    weightedScore: number;
  };
}

export interface BenchmarkWeights {
  mmlu: number;
  humaneval: number;
  gpqa: number;
  arena_elo: number;
}

interface ModelData {
  model_id: string;
  display_name: string;
  params_b: number;
  family: string;
  meta?: {
    category?: string;
    modality?: string;
  };
  benchmarks?: {
    mmlu?: number;
    humaneval?: number;
    gpqa?: number;
    arena_elo?: number;
  };
}

// Benchmark normalization constants (max values for scaling)
const BENCHMARK_MAX = {
  mmlu: 88.7,
  humaneval: 92.0,
  gpqa: 55.0,
  arena_elo: 1310,
};

/**
 * Get benchmark weights based on persona expertise types
 */
function getAggregateWeights(personas: WizardPersona[]): BenchmarkWeights {
  const expertiseWeights: Record<string, BenchmarkWeights> = {
    coding: { humaneval: 0.6, mmlu: 0.4, gpqa: 0, arena_elo: 0 },
    thinking: { gpqa: 0.6, mmlu: 0.4, humaneval: 0, arena_elo: 0 },
    'long-conversation': { arena_elo: 0.7, mmlu: 0.3, humaneval: 0, gpqa: 0 },
    summarization: { arena_elo: 0.6, mmlu: 0.4, humaneval: 0, gpqa: 0 },
    general: { arena_elo: 1.0, mmlu: 0, humaneval: 0, gpqa: 0 },
  };

  if (personas.length === 0) {
    return expertiseWeights.general;
  }

  // Aggregate weights based on persona mix (equal weight for now)
  const aggregated: BenchmarkWeights = {
    mmlu: 0,
    humaneval: 0,
    gpqa: 0,
    arena_elo: 0,
  };

  personas.forEach(persona => {
    const weight = 1 / personas.length;
    const expertiseType = persona.expertiseType || 'general';
    const weights = expertiseWeights[expertiseType] || expertiseWeights.general;

    aggregated.mmlu += weights.mmlu * weight;
    aggregated.humaneval += weights.humaneval * weight;
    aggregated.gpqa += weights.gpqa * weight;
    aggregated.arena_elo += weights.arena_elo * weight;
  });

  return aggregated;
}

/**
 * Calculate benchmark-based score for a model
 */
function calculateBenchmarkScore(
  model: ModelData,
  weights: BenchmarkWeights
): { score: number; breakdown: ModelScore['benchmarkBreakdown'] } | null {
  if (!model.benchmarks) {
    return null;
  }

  const { mmlu, humaneval, gpqa, arena_elo } = model.benchmarks;

  // Calculate weighted score
  let weightedScore = 0;
  let totalWeight = 0;

  if (mmlu !== undefined && weights.mmlu > 0) {
    weightedScore += (mmlu / BENCHMARK_MAX.mmlu) * 100 * weights.mmlu;
    totalWeight += weights.mmlu;
  }

  if (humaneval !== undefined && weights.humaneval > 0) {
    weightedScore += (humaneval / BENCHMARK_MAX.humaneval) * 100 * weights.humaneval;
    totalWeight += weights.humaneval;
  }

  if (gpqa !== undefined && weights.gpqa > 0) {
    weightedScore += (gpqa / BENCHMARK_MAX.gpqa) * 100 * weights.gpqa;
    totalWeight += weights.gpqa;
  }

  if (arena_elo !== undefined && weights.arena_elo > 0) {
    weightedScore += (arena_elo / BENCHMARK_MAX.arena_elo) * 100 * weights.arena_elo;
    totalWeight += weights.arena_elo;
  }

  if (totalWeight === 0) {
    return null;
  }

  const normalizedScore = weightedScore / totalWeight;

  return {
    score: normalizedScore,
    breakdown: {
      mmlu,
      humaneval,
      gpqa,
      arena_elo,
      weightedScore: normalizedScore,
    },
  };
}

/**
 * Calculate model recommendations based on benchmarks and environment
 */
export function scoreModels(
  models: ModelData[],
  environment: WizardEnvironment,
  personas: WizardPersona[]
): ModelScore[] {
  const weights = getAggregateWeights(personas);
  
  const scores: ModelScore[] = models.map(model => {
    const reasoning: string[] = [];
    const strengths: string[] = [];
    const considerations: string[] = [];
    
    // Try benchmark-based scoring first
    const benchmarkResult = calculateBenchmarkScore(model, weights);
    
    let baseScore = 50;
    let benchmarkBreakdown: ModelScore['benchmarkBreakdown'];

    if (benchmarkResult) {
      baseScore = benchmarkResult.score;
      benchmarkBreakdown = benchmarkResult.breakdown;
      reasoning.push('Score calculated from benchmark performance');
      
      // Add strengths based on benchmark scores
      if (model.benchmarks?.mmlu && model.benchmarks.mmlu > 80) {
        strengths.push('Strong reasoning and general knowledge (MMLU > 80)');
      }
      if (model.benchmarks?.humaneval && model.benchmarks.humaneval > 80) {
        strengths.push('Excellent code generation capabilities (HumanEval > 80)');
      }
      if (model.benchmarks?.gpqa && model.benchmarks.gpqa > 45) {
        strengths.push('Advanced reasoning on complex problems (GPQA > 45)');
      }
    } else {
      // Fallback to heuristic scoring for models without benchmarks
      const modelSize = model.params_b;
      
      // Size-based heuristics
      if (modelSize >= 70) {
        baseScore = 75;
        strengths.push('Large model suitable for complex tasks');
      } else if (modelSize >= 34) {
        baseScore = 65;
        strengths.push('Mid-sized model with good capability balance');
      } else if (modelSize >= 13) {
        baseScore = 55;
        strengths.push('Efficient model for standard workloads');
      } else {
        baseScore = 45;
        strengths.push('Lightweight model for simple tasks');
      }
      
      reasoning.push('Score estimated from model size (benchmarks unavailable)');
      considerations.push('Benchmark data not available for precise comparison');
    }

    let finalScore = baseScore;

    // Apply latency penalties/bonuses
    const modelSize = model.params_b;
    if (environment.latencyTolerance === 'snappy') {
      if (modelSize <= 13) {
        finalScore += 15;
        strengths.push('Fast inference for snappy response requirements');
        reasoning.push('Small model optimized for low latency');
      } else if (modelSize >= 70) {
        finalScore -= 15;
        considerations.push('Large model may not meet snappy latency requirements');
        reasoning.push('Model size may impact response time');
      }
    } else if (environment.latencyTolerance === 'batch') {
      if (modelSize >= 34) {
        finalScore += 10;
        strengths.push('Can leverage larger model in batch processing');
        reasoning.push('Batch processing allows for quality-focused model');
      }
    }

    // Apply concurrency considerations
    const concurrentUsers = environment.concurrentUsers || 1000;
    if (concurrentUsers > 5000) {
      if (modelSize <= 13) {
        finalScore += 10;
        strengths.push('Efficient model scales well with high concurrency');
        reasoning.push('High concurrency workload benefits from efficient model');
      }
    } else if (concurrentUsers < 500) {
      if (modelSize >= 34) {
        finalScore += 5;
        strengths.push('Low concurrency allows for premium model selection');
      }
    }

    // Normalize final score to 0-100 range
    finalScore = Math.max(0, Math.min(100, finalScore));

    return {
      modelId: model.model_id,
      score: finalScore,
      reasoning,
      strengths,
      considerations,
      benchmarkBreakdown,
    };
  });

  // Sort by score descending
  return scores.sort((a, b) => b.score - a.score);
}

/**
 * Generate explanation for top recommendations
 */
export function generateRecommendationSummary(
  topModels: ModelScore[],
  environment: WizardEnvironment,
  personas: WizardPersona[]
): string {
  // Fallback to reasonable defaults if concurrentUsers is not specified
  const concurrentUsers =
    environment.concurrentUsers ??
    environment.totalActiveUsersPerDay ??
    1000;
  
  const summary = [
    `Based on your configuration with ${personas.length} persona(s) and ${concurrentUsers.toLocaleString()} expected concurrent users,`,
    `we recommend the following models:`
  ];

  if (environment.latencyTolerance === 'snappy') {
    summary.push('Your snappy latency requirements favor fast, responsive models.');
  } else if (environment.latencyTolerance === 'batch') {
    summary.push('Your batch processing constraints allow for larger, more capable models.');
  }

  if (environment.regulatorySensitivity === 'high') {
    summary.push('High data sensitivity requires careful security and compliance considerations.');
  }

  if (concurrentUsers > 5000) {
    summary.push('High concurrency patterns favor efficient, scalable models.');
  }

  return summary.join(' ');
}
